import {
  Button,
  Center,
  getDefaultZIndex,
  Group,
  Image,
  Modal,
  Title,
} from '@mantine/core';
import React, { ReactNode, useState } from 'react';

import { useI18n } from '../../contexts/I18nProvider';
import FileViewer from './FileViewer';

export type FilePreview = {
  id: string;
  title?: ReactNode;
  srcUrl: string;
  fileName: string;
  /** `image` affiche une <Image> centrée, sinon le fichier est rendu dans une iframe. */
  kind?: 'file' | 'image';
  /** Faux pour les types non prévisualisables, affichés dans une modale de taille normale. */
  fullScreen?: boolean;
  /** Bouton de téléchargement optionnel, affiché sous l'aperçu. */
  download?: { label: string; onDownload: () => void };
};

// Le gestionnaire de modales de Mantine (`modals.open`) ne rend qu'une seule <Modal> à la fois,
// alimentée par le haut de sa pile : empiler une modale démonte l'arbre React de celle du dessous.
// L'aperçu doit donc rester au-dessus d'elle sans passer par la pile.
const FILE_PREVIEW_Z_INDEX = getDefaultZIndex('modal') + 2;

/**
 * Aperçu de fichier rendu dans l'arbre React de l'écran appelant, et non via `modals.open`.
 *
 * Sans cela, ouvrir un aperçu depuis un écran lui-même affiché en modale (import des bulletins
 * d'un client côté gestionnaire de paie, détail d'une note de frais…) démonte cet écran : à la
 * fermeture de l'aperçu il est remonté à neuf et perd son état — onglet actif, mois sélectionné,
 * filtres, position de défilement.
 *
 * L'appelant rend `filePreviewModal` dans son JSX et déclenche `openFilePreview` à la demande.
 */
export default function useFilePreviewModal() {
  const { t } = useI18n();
  const [preview, setPreview] = useState<FilePreview | null>(null);

  function close() {
    setPreview(null);
  }

  const filePreviewModal = (
    <Modal
      opened={preview !== null}
      onClose={close}
      fullScreen={preview?.fullScreen !== false}
      size={'xl'}
      zIndex={FILE_PREVIEW_Z_INDEX}
      transitionProps={{ transition: 'slide-down' }}
      title={
        preview?.title ? (
          <Title size={'h3'} component="p">
            {preview.title}
          </Title>
        ) : undefined
      }
    >
      {preview && (
        <>
          {preview.kind === 'image' ? (
            <Center h={'calc(100vh - 160px)'}>
              <Image
                src={preview.srcUrl}
                fit="contain"
                mah={'100%'}
                maw={'100%'}
                w={'auto'}
                h={'auto'}
              />
            </Center>
          ) : (
            <FileViewer
              id={preview.id}
              srcUrl={preview.srcUrl}
              title={preview.fileName}
            />
          )}
          {preview.download && (
            <Group justify={'flex-end'} mt={'md'}>
              <Button variant={'default'} onClick={close}>
                {t('w.cancel')}
              </Button>
              <Button
                onClick={() => {
                  preview.download?.onDownload();
                  close();
                }}
              >
                {preview.download.label}
              </Button>
            </Group>
          )}
        </>
      )}
    </Modal>
  );

  return { openFilePreview: setPreview, filePreviewModal };
}
